<script lang="ts">
	import {
		Button,
		Container,
		Input,
		Modal,
		Text,
		BulletedList,
		Stack,
	} from '@Shelly/shelly-design-system/index';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { profile_data } from 'src/stores/account/user';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { clonableSettings } from 'src/helpers/settings/settings_config_helpers';
	import { formatTemplateValue } from 'src/helpers/settings/template_helpers';
	import { get_gen_2_component_channel_structure } from 'src/helpers/interface_helpers';

	export let showModal = false;
	export let device: Device;

	let name = '';
	let notes = '';
	let showOverrideConfirmation = false;

	const deviceConfig = devices_settings.getByRealId(device.id)?.[
		get_gen_2_component_channel_structure(device)
	];

	const inputConfig = devices_settings.getByRealId(device.id)?.[`input:${device.channel}`];

	type FavConfig = {
		id: string;
		name: string;
		notes?: string;
		config: Record<string, any>;
		inputConfig?: Record<string, any>;
		createdAt: string;
		deviceId: string;
		deviceType?: string;
	};

	$: existingTemplateId =
		Object.values($profile_data.fav_configs ?? {}).find(
			(template): template is FavConfig =>
				(template as FavConfig).id !== undefined &&
				(template as FavConfig).id.startsWith(`${device.id}_`),
		)?.id ?? null;

	$: isSaveDisabled = !name.trim();

	function resetModals() {
		showModal = false;
		showOverrideConfirmation = false;
		name = '';
		notes = '';
	}

	function handleSaveClick() {
		existingTemplateId ? (showOverrideConfirmation = true) : saveTemplate();
	}
	async function saveTemplate() {
		const templateId = existingTemplateId || `${device.id}_${Math.floor(Date.now() / 1000)}`;

		const newTemplate = {
			id: templateId,
			name: name.trim(),
			notes: notes.trim(),
			config: deviceConfig,
			inputConfig: inputConfig,
			createdAt: new Date().toISOString(),
			deviceId: device.id,
			deviceType: device.type,
		};

		const updatedTemplates = {
			...($profile_data.fav_configs ?? {}),
			[templateId]: newTemplate,
		};

		profile_data.set({
			...$profile_data,
			fav_configs: updatedTemplates,
		});

		const response = await save_profile('fav_configs', updatedTemplates);

		if (response?.isok) {
			onSettingsResponse({ isok: true }, device, {
				success: $format('success_config_saved', {
					default: 'Configuration saved successfully',
				}),
			});
			resetModals();
		} else {
			onSettingsResponse(response, device, {
				danger: $format('error_config_save_failed', {
					default: 'Failed to save configuration. Please try again.',
				}),
			});
		}
	}
</script>

<Modal
	on:close={resetModals}
	{showModal}
	title={$format('save_template_modal_title', {
		default: 'Save Device Configuration',
	})}
>
	<Container slot="content">
		<Stack gap="m">
			<Text element="p">
				{$format('save_template_modal_description', {
					default:
						'Give a name and optional notes for your current settings so you can reuse them later.',
				})}
			</Text>

			{#if deviceConfig}
				<Text element="h3" class="mb-s">
					{$format('save_template_modal_settings_heading', {
						default: 'Settings to be saved:',
					})}
				</Text>
				<Stack gap="s" class="mb-m">
					{#each clonableSettings() as setting}
						{#if setting.key in deviceConfig}
							<BulletedList
								titleStyleType="body-small"
								descriptionStyleType="body-small"
								list={[
									{
										title: setting.title,
										description: formatTemplateValue(
											setting.key,
											deviceConfig[setting.key],
										),
									},
								]}
							></BulletedList>
						{/if}
					{/each}
				</Stack>
			{/if}

			<Input
				icon={null}
				type="text"
				label={$format('save_template_modal_name_label', { default: 'Name' })}
				bind:value={name}
				id="template-name-input-ijnadilua"
				aria-labelledby="template-name-input-ijnadilua"
			/>
			<Input
				icon={null}
				type="text"
				label={$format('save_template_modal_notes_label', {
					default: 'Notes (optional)',
				})}
				bind:value={notes}
				id="template-notes-input-uhdoluh"
				aria-labelledby="template-notes-input-uhdoluh"
			/>
		</Stack>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button variant="secondary" class="min-width-md-s text-nowrap mr-s" on:click={resetModals}>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button class="min-width-md-s text-nowrap" on:click={handleSaveClick} disabled={isSaveDisabled}>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>

<Modal
	on:close={() => (showOverrideConfirmation = false)}
	showModal={showOverrideConfirmation}
	title={$format('save_template_override_title', {
		default: 'Override Existing Configuration?',
	})}
>
	<Container slot="content">
		<Text>
			{$format('save_template_override_description', {
				default: 'A configuration already exists for this device. Do you want to override it?',
			})}
		</Text>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-s text-nowrap mr-s"
			on:click={() => (showOverrideConfirmation = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button class="min-width-md-s text-nowrap" on:click={saveTemplate}>
			{$format('button_yes', { default: 'Yes' })}
		</Button>
	</div>
</Modal>
